import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getTypeConfig } from "@/constants/ui-mapping";
import type { Tables } from "@/integrations/supabase/types";

type DocumentType = Tables<"documents">;

interface TaxonomyType {
  slug: string;
  label_fr: string;
  family: string;
  emoji: string;
  core_type: string;
  sort_order: number;
}

// Family labels in French for display
const FAMILY_LABELS: Record<string, string> = {
  revenue: "💰 Revenu / Fiscalité",
  identity: "🆔 Identité & État civil",
  housing: "🏠 Domicile & Immobilier",
  banking: "🏦 Bancaire & Crédit",
  work: "💼 Professionnel / Entreprise",
  education: "🎓 Formation / Scolarité",
  vehicle: "🚗 Véhicule & Transport",
  health: "🏥 Santé",
  consumption: "📦 Consommation / Divers",
  other: "📁 Autres",
};

interface QuickTypeEditProps {
  document: DocumentType;
  onUpdate: (updatedDoc: DocumentType) => void;
}

export function QuickTypeEdit({ document, onUpdate }: QuickTypeEditProps) {
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [taxonomy, setTaxonomy] = useState<TaxonomyType[]>([]);
  const [loading, setLoading] = useState(false);

  // Load taxonomy from database when dialog opens (reload each time for fresh data)
  useEffect(() => {
    if (open) {
      loadTaxonomy();
    }
  }, [open]);

  const loadTaxonomy = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("document_taxonomy")
      .select("slug, label_fr, family, emoji, core_type, sort_order")
      .eq("active", true)
      .order("family")
      .order("sort_order")
      .order("label_fr");

    if (error) {
      console.error("Error loading taxonomy:", error);
      toast.error("Erreur lors du chargement des types");
    } else {
      setTaxonomy(data || []);
    }
    setLoading(false);
  };

  // Group taxonomy by family
  const groupedTaxonomy = useMemo(() => {
    const groups: Record<string, TaxonomyType[]> = {};
    
    taxonomy.forEach((t) => {
      if (!groups[t.family]) {
        groups[t.family] = [];
      }
      groups[t.family].push(t);
    });

    return groups;
  }, [taxonomy]);

  // Get current display label
  const currentLabel = useMemo(() => {
    // Priority 1: taxonomy_slug if present
    if (document.taxonomy_slug) {
      const t = taxonomy.find((tx) => tx.slug === document.taxonomy_slug);
      if (t) {
        return `${t.emoji} ${t.label_fr}`;
      }
    }
    // Priority 2: fallback to doc_type (core 23)
    const cfg = getTypeConfig(document.doc_type);
    return `${cfg.emoji} ${cfg.label}`;
  }, [document.taxonomy_slug, document.doc_type, taxonomy]);

  const handleTypeChange = async (selected: TaxonomyType) => {
    // Skip if already selected
    if (selected.slug === document.taxonomy_slug) {
      setOpen(false);
      return;
    }

    setSaving(true);

    // Save correction for AI feedback loop
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (user) {
      await supabase.from("document_corrections").insert({
        document_id: document.id,
        user_id: user.id,
        field_name: "taxonomy_slug",
        original_value: document.taxonomy_slug || document.doc_type,
        corrected_value: selected.slug,
      });
    }

    // Update document: taxonomy_slug + doc_type (core fallback)
    const { error } = await supabase
      .from("documents")
      .update({
        taxonomy_slug: selected.slug,
        doc_type: selected.core_type as any,
        classification_method: "manual",
      })
      .eq("id", document.id);

    if (error) {
      console.error("Error updating document type:", error);
      toast.error("Erreur lors de la modification");
      setSaving(false);
      return;
    }

    toast.success("Type de document modifié");
    onUpdate({
      ...document,
      taxonomy_slug: selected.slug,
      doc_type: selected.core_type as any,
      classification_method: "manual",
    });
    setSaving(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && setOpen(v)}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className="h-auto px-2 py-1 text-base font-semibold hover:bg-accent gap-1"
          disabled={saving}
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              {currentLabel}
              <ChevronDown className="w-4 h-4 opacity-50" />
            </>
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Changer le type du document</DialogTitle>
        </DialogHeader>

        <Command className="rounded-none border-0">
          <CommandInput placeholder="Rechercher un type…" />
          <CommandList className="max-h-[60vh]">
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <CommandEmpty>Aucun type trouvé.</CommandEmpty>

                {Object.entries(groupedTaxonomy).map(([family, types]) => (
                  <CommandGroup
                    key={family}
                    heading={FAMILY_LABELS[family] || family}
                  >
                    {types.map((t) => {
                      const selected = document.taxonomy_slug === t.slug;
                      return (
                        <CommandItem
                          key={t.slug}
                          value={`${t.label_fr} ${t.slug} ${family}`}
                          onSelect={() => handleTypeChange(t)}
                        >
                          <span className="mr-2">{t.emoji}</span>
                          <span className="flex-1">{t.label_fr}</span>
                          {selected ? (
                            <Check className="w-4 h-4 text-primary" />
                          ) : null}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))}
              </>
            )}
          </CommandList>
        </Command>

        <div className="px-6 pb-6 pt-3 flex justify-end">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={saving}
          >
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
